import React, { useEffect, useMemo, useState } from 'react';
import { SearchBarWithNumber } from '@components/SearchbarWithNumber';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Button, Menu, MenuItem, Stack } from '@mui/material';
import { FormGroup, FormLabel } from '@mui/material';
import { Container, FilterChips } from '@components/Filter';
import { FilterButton } from '@components/Filter/FilterButton';
import { FilterDrawer } from '@components/Filter/FilterDrawer';
import { hasValue } from '@components/Filter/filter.utils';
import { FlexRow } from '@components/FlexRow';
import FeatureCreditsDisabler from '@modules/features/FeatureCreditsDisabler';
import { CreditsFeature } from '@utils/permissions';
import { useQuery } from '@apollo/client';
import { GET_SECTORS } from '@gql/sectors';
import InputAutocomplete from '@components/InputAutocomplete';
import AutoCompletePro from '@components/AutoComplete/AutoCompletePro';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { BrandsFilter as BrandsFilterType } from './hooks/useGetBrands';

interface BrandsFilterForm {
    isFeatured: string; // '' | 'true' | 'false'
    sector: string[];
    proId: string; // single Pro ID
}

const defaultBrandsFilterForm: BrandsFilterForm = {
    isFeatured: '',
    sector: [],
    proId: '',
};

const brandsFilterLabels: Record<string, string> = {
    isFeatured: 'Vedette',
    sector: 'Secteur',
    proId: 'Établissement',
};

const filterToForm = (filter: BrandsFilterType): BrandsFilterForm => ({
    isFeatured: filter.isFeatured === true ? 'true' : filter.isFeatured === false ? 'false' : '',
    sector: filter.sector ?? [],
    proId: filter.proId?.[0] ?? '',
});

const formToFilter = (form: BrandsFilterForm): BrandsFilterType => {
    const result: BrandsFilterType = {};
    if (form.isFeatured === 'true') result.isFeatured = true;
    else if (form.isFeatured === 'false') result.isFeatured = false;
    if (form.sector?.length) result.sector = form.sector;
    if (form.proId) result.proId = [form.proId];
    return result;
};

export interface BrandsFilterProps {
    totalDocs: number;
    selectedIds: string[];
    isUpdatingFeatured: boolean;
    proId?: string;
    filter: BrandsFilterType;
    onQueryChange: (value: string) => void;
    onFilterChange: (filter: BrandsFilterType) => void;
    onAddBrand: () => void;
    onBulkFeaturedUpdate: (isFeatured: boolean) => void;
    onBulkDeleteSelected: () => void;
}

const BrandsFilter = ({
    totalDocs,
    selectedIds,
    isUpdatingFeatured,
    proId,
    filter,
    onQueryChange,
    onFilterChange,
    onAddBrand,
    onBulkFeaturedUpdate,
    onBulkDeleteSelected,
}: BrandsFilterProps) => {
    const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [proResetKey, setProResetKey] = useState(0);
    const [selectedProName, setSelectedProName] = useState<string | undefined>(undefined);

    const { data: sectorsData } = useQuery(GET_SECTORS);
    const sectors = useMemo(() => sectorsData?.getSectors ?? [], [sectorsData]);

    const sectorOptions = useMemo(
        () => sectors.map((s: any) => ({ label: s.sectorName, value: s._id })),
        [sectors],
    );

    const isFeaturedOptions = useMemo(
        () => [
            { label: 'En vedette', value: 'true' },
            { label: 'Non en vedette', value: 'false' },
        ],
        [],
    );

    const methods = useForm<BrandsFilterForm>({
        defaultValues: filterToForm(filter),
    });

    useEffect(() => {
        methods.reset(filterToForm(filter));
        if (!filter.proId?.length) {
            setProResetKey((k) => k + 1);
            setSelectedProName(undefined);
        }
    }, [filter, methods]);

    const chipLabels = useMemo(
        () => ({
            isFeatured: [
                { id: 'true', value: 'true', label: 'En vedette' },
                { id: 'false', value: 'false', label: 'Non en vedette' },
            ],
            sector: sectors.map((s: any) => ({ id: s._id, value: s._id, label: s.sectorName })),
        }),
        [sectors],
    );

    const displayFilter = useMemo(() => filterToForm(filter), [filter]);
    const hasActiveFilters = useMemo(() => Object.values(filter).some(hasValue), [filter]);

    const toggleDrawer = (open: boolean) => () => setIsDrawerOpen(open);

    const onApplyFilter = (data: BrandsFilterForm) => {
        onFilterChange(formToFilter(data));
        setIsDrawerOpen(false);
    };

    const onResetFilter = () => {
        methods.reset(defaultBrandsFilterForm);
        setProResetKey((k) => k + 1);
        onFilterChange({});
        setIsDrawerOpen(false);
    };

    const hasSelection = selectedIds.length > 0;

    const handleOpenActionsMenu = (event: React.MouseEvent<HTMLElement>) => {
        setActionsMenuAnchor(event.currentTarget);
    };

    const handleCloseActionsMenu = () => {
        setActionsMenuAnchor(null);
    };
    return (
        <FormProvider {...methods}>
            <Stack spacing={2} sx={{ mb: 3 }}>
                <Container>
                    <FlexRow sx={{ gap: 2 }}>
                        <SearchBarWithNumber totalCount={totalDocs} onChange={onQueryChange} />
                        <FilterButton toggleDrawer={toggleDrawer} hasActiveFilters={hasActiveFilters} />
                    </FlexRow>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            color="secondary"
                            endIcon={<KeyboardArrowDownIcon />}
                            onClick={handleOpenActionsMenu}
                            disabled={!hasSelection}>
                            Actions
                        </Button>
                        <Menu
                            anchorEl={actionsMenuAnchor}
                            open={Boolean(actionsMenuAnchor)}
                            onClose={handleCloseActionsMenu}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                            <MenuItem
                                onClick={() => {
                                    onBulkFeaturedUpdate(true);
                                    handleCloseActionsMenu();
                                }}
                                disabled={!hasSelection || isUpdatingFeatured}>
                                Mettre en vedette
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    onBulkFeaturedUpdate(false);
                                    handleCloseActionsMenu();
                                }}
                                disabled={!hasSelection || isUpdatingFeatured}>
                                Retirer la vedette
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    onBulkDeleteSelected();
                                    handleCloseActionsMenu();
                                }}
                                disabled={!hasSelection}
                                sx={{ color: 'error.main' }}>
                                Supprimer la sélection
                            </MenuItem>
                        </Menu>
                        <FeatureCreditsDisabler
                            proId={proId || ''}
                            feature={CreditsFeature.BRAND_CREDITS}
                            showLockIcon
                            alert={{
                                title: 'Nombre maximum de crédits marque atteint',
                                message: "Vous n'avez plus de crédits disponibles pour créer une marque.",
                            }}>
                            <Button variant="contained" color="primary" size="large" onClick={onAddBrand}>
                                <i className="fi fi-br-plus" style={{ marginRight: 8 }} />
                                Ajouter
                            </Button>
                        </FeatureCreditsDisabler>
                    </Stack>
                </Container>

                {hasActiveFilters && (
                    <Container>
                        <FlexRow>
                            <FilterChips
                                filter={displayFilter}
                                labels={brandsFilterLabels}
                                defaultFilters={defaultBrandsFilterForm}
                                onFilterChange={(formFilter: BrandsFilterForm) => {
                                    onFilterChange(formToFilter(formFilter));
                                }}
                                chipLabels={chipLabels}
                                renderLabel={({ key, label }) => {
                                    if (key === 'proId') return selectedProName ? `${label}: ${selectedProName}` : `${label}: sélectionné`;
                                    return undefined;
                                }}
                            />
                        </FlexRow>
                    </Container>
                )}
            </Stack>

            <FilterDrawer
                isDrawerOpen={isDrawerOpen}
                toggleDrawer={toggleDrawer}
                onApplyFilter={onApplyFilter}
                onResetFilter={onResetFilter}>
                <FormGroup>
                    <FormLabel>{brandsFilterLabels.isFeatured}</FormLabel>
                    <InputAutocomplete name="isFeatured" options={isFeaturedOptions} />
                </FormGroup>

                <FormGroup>
                    <FormLabel>{brandsFilterLabels.sector}</FormLabel>
                    <InputAutocomplete name="sector" options={sectorOptions} multiple />
                </FormGroup>

                <FormGroup>
                    <FormLabel>{brandsFilterLabels.proId}</FormLabel>
                    <Controller
                        name="proId"
                        control={methods.control}
                        render={({ field }) => (
                            <AutoCompletePro
                                key={proResetKey}
                                onChange={(id, commercialName) => {
                                    field.onChange(id ?? '');
                                    setSelectedProName(commercialName);
                                }}
                            />
                        )}
                    />
                </FormGroup>
            </FilterDrawer>
        </FormProvider>
    );
};

export default BrandsFilter;
